'use client';
import { Fragment, useLayoutEffect, useRef, useState, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { CodeBlock, CodeBlockActions, CodeBlockContent, CodeBlockCopyButton, CodeBlockHeader, CodeBlockLine, CodeBlockTitle } from '../../components/ui/shadcn/code-block';
import { useHighlightedCode } from '../../lib/use-highlighted-code';
import { useTranslation } from '../../lib/i18n';
import { markdownDocument } from '../../lib/markdown';
import type { BrainMessage, Profile } from '../../lib/types';
import { MentionChip, useMentionTokens } from '../../components/ui/MentionChip';
import { MENTION_SLOT, markMentions, mentionKey, type MentionToken } from 'elowen-plugin-shared/composerMentions';
import { AGENT_AVATAR_SRC } from './agentIdentity';

type Code = { value: string; language: string };
type Block = { html: string | null; text: string | null; element: ReactElement; codes: readonly Code[] };

const NO_TOKENS: readonly MentionToken[] = [];

/** Keep React elements as well as DOM: memoizing a block body alone still constructs and reconciles the
 * entire prefix on every token. The cache is component state, so an abandoned render cannot publish it. */
function renderBlocks(text: string, tokens: readonly MentionToken[], previous: readonly Block[]) {
  const body = markdownDocument(text);
  markMentions(body, tokens);
  for (const node of body.querySelectorAll('[data-chat-code]')) node.removeAttribute('data-chat-code');
  const blocks = Array.from(body.childNodes, (node, index): Block => {
    const html = node.nodeType === Node.ELEMENT_NODE ? (node as Element).outerHTML : null;
    const content = html === null ? node.textContent : null;
    const old = previous[index];
    if (old && old.html === html && old.text === content) return old;
    const codes: Code[] = [];
    if (node instanceof Element) {
      const pres = node.matches('pre') ? [node] : [...node.querySelectorAll('pre')];
      for (const pre of pres) {
        const code = pre.querySelector(':scope > code');
        if (!code) continue;
        codes.push({ value: code.textContent ?? '', language: [...code.classList].find(name => name.startsWith('language-'))?.slice('language-'.length) ?? '' });
        const slot = body.ownerDocument.createElement('div');
        slot.setAttribute('data-chat-code', '');
        pre.replaceWith(slot);
        if (pre === node) return { html, text: content, codes, element: <div key={index} className="chat-markdown-block contents"><div data-chat-code="" /></div> };
      }
    }
    return { html, text: content, codes, element: html === null
      ? <Fragment key={index}>{content}</Fragment>
      : <div key={index} className="chat-markdown-block contents" dangerouslySetInnerHTML={{ __html: (node as Element).outerHTML }} /> };
  });
  return { text, tokens, blocks };
}

/** The mention slots in the rendered blocks; `previous` itself while they are the same elements. */
function slotsIn(root: HTMLElement, previous: readonly HTMLElement[]): readonly HTMLElement[] {
  const found = [...root.querySelectorAll<HTMLElement>(`[${MENTION_SLOT}]`)];
  return found.length === previous.length && found.every((slot, i) => slot === previous[i]) ? previous : found;
}

function MarkdownCode({ code }: { code: Code }) {
  const { t } = useTranslation();
  const lines = useHighlightedCode(code.value, code.language);
  return <CodeBlock className="my-3">
    <CodeBlockHeader>
      {code.language ? <CodeBlockTitle>{code.language}</CodeBlockTitle> : null}
      <CodeBlockActions><CodeBlockCopyButton value={code.value} title={t.brainChat.ux.codeCopy} /></CodeBlockActions>
    </CodeBlockHeader>
    <CodeBlockContent>{lines.map((line, index) => <CodeBlockLine key={index}>{line}</CodeBlockLine>)}</CodeBlockContent>
  </CodeBlock>;
}

/** One renderer for live and stored assistant text on every chat surface. Parse the complete document:
 * references and unfinished Markdown can still revise earlier blocks. Only changed HTML reaches the DOM.
 * Display:contents boundaries preserve the flow, selections and horizontal scroll of completed blocks.
 *
 * A message's structured `mentions` become chips in its prose: the sanitized document keeps an empty slot
 * per written token (`markMentions`), and once the blocks are in the DOM each slot receives a `MentionChip`
 * through a portal, before paint, so the chip is the shared React component with its avatar rather than an
 * HTML copy of it. Without mentions nothing is scanned. */
export function ChatMarkdown({ text, className = '', mentions, people, viewerId }: {
  text: string;
  className?: string;
  /** The message's structured mentions (the agent's `Address`); absent, no `@` is a mention. */
  mentions?: BrainMessage['mentions'];
  /** The conversation's members by account id: the chips' current names and avatars. */
  people?: ReadonlyMap<number, Profile>;
  /** The signed-in account, whose chip is drawn a touch stronger. */
  viewerId?: number;
}) {
  const resolved = useMentionTokens(mentions, people);
  const tokens = resolved.length ? resolved : NO_TOKENS;
  const { t } = useTranslation();
  const [previous, setPrevious] = useState(() => renderBlocks(text, tokens, []));
  const current = previous.text === text && previous.tokens === tokens ? previous : renderBlocks(text, tokens, previous.blocks);
  if (current !== previous) setPrevious(current);
  const root = useRef<HTMLDivElement>(null);
  const [slots, setSlots] = useState<readonly HTMLElement[]>([]);
  const [codes, setCodes] = useState<readonly { slot: HTMLElement; code: Code }[]>([]);
  // New blocks (`current`) may have replaced a block's HTML, and with it the slots inside.
  useLayoutEffect(() => {
    const next = tokens.length ? slotsIn(root.current!, slots) : [];
    if (next !== slots && (next.length || slots.length)) setSlots(next);
    const values = current.blocks.flatMap(block => block.codes);
    const found = [...root.current!.querySelectorAll<HTMLElement>('[data-chat-code]')].map((slot, index) => {
      const code = values[index];
      if (!code) throw new Error('Markdown code slot has no sanitized source');
      return { slot, code };
    });
    if (found.length !== codes.length || found.some((entry, i) => entry.slot !== codes[i]?.slot || entry.code !== codes[i]?.code)) setCodes(found);
  }, [current, tokens.length, slots, codes]);
  return (
    <div ref={root} className={`chat-markdown inline-links text-conversation text-foreground ${className}`}>
      {current.blocks.map(block => block.element)}
      {codes.map(({ slot, code }, i) => createPortal(<MarkdownCode code={code} />, slot, `code-${i}`))}
      {slots.map((slot, i) => {
        const token = tokens.find(candidate => mentionKey(candidate) === slot.getAttribute(MENTION_SLOT) && candidate.label === slot.dataset.mentionLabel);
        return token ? createPortal(<MentionChip token={token} viewerId={viewerId} prefix={t.chat.mentionChip} agentSrc={AGENT_AVATAR_SRC} />, slot, String(i)) : null;
      })}
    </div>
  );
}
